import { auth } from '@/lib/firebase';
import { getIdToken } from 'firebase/auth';

const ANALYTICS_BASE = import.meta.env.VITE_ANALYTICS_API_URL ?? '';

let _tenantId: string | null = null;
let _siteId: string | null = null;

export interface AnalyticsSiteOption {
  id: string;
  name: string;
}

export interface AnalyticsTenantOption {
  id: string;
  name: string;
  timezone?: string;
  sites: AnalyticsSiteOption[];
}

export interface AiQueryResponse {
  matched: boolean;
  metricId?: string;
  metricName?: string;
  sessionId: string;
  answer?: string;
  chartType?: 'bar' | 'line' | 'table' | 'stat';
  rows?: Record<string, unknown>[];
  schema?: Array<{ name: string; type: string }>;
  extractedParams?: {
    dateRange: { start: string; end: string } | null;
    dimension: string | null;
    filterField: string | null;
    filterValue: string | null;
  };
  closestMetrics?: string[];
  fallback?: {
    message: string;
    closestMetrics: Array<{ id: string; name: string; description: string }>;
    buildMetricCTA: boolean;
  };
}

export interface AiChatSession {
  id: string;
  lastMessageAt: string | null;
  tenantId: string;
  userId: string;
}

export function setAnalyticsScope(tenantId: string | null, siteId?: string | null) {
  _tenantId = tenantId;
  _siteId = siteId ?? null;
}

export function setAnalyticsTenant(id: string | null) {
  _tenantId = id;
}

async function analyticsRequest<T>(
  path: string,
  options: { method?: string; params?: Record<string, string>; body?: unknown } = {},
): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await getIdToken(user);

  const fullPath = `${ANALYTICS_BASE}${path}`;
  const url = ANALYTICS_BASE.startsWith('http')
    ? new URL(fullPath)
    : new URL(fullPath, window.location.origin);
  if (options.params) {
    Object.entries(options.params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  if (_tenantId) headers['X-Tenant-ID'] = _tenantId;
  if (_siteId) headers['X-Site-ID'] = _siteId;

  const res = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers,
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const analyticsApi = {
  mrr: (period: string) =>
    analyticsRequest('/analytics/mrr', { params: { period } }),
  disputes: (period: string) =>
    analyticsRequest('/analytics/disputes', { params: { period } }),
  payments: (period: string) =>
    analyticsRequest('/analytics/payments', { params: { period } }),
  dunning: (period: string, processor?: string[]) =>
    analyticsRequest('/analytics/dunning', {
      params: {
        period,
        ...(processor?.length ? { processor: processor.join(',') } : {}),
      },
    }),
  revenue: (period: string) =>
    analyticsRequest('/analytics/revenue', { params: { period } }),
  subscriptions: (period: string) =>
    analyticsRequest('/analytics/subscriptions', { params: { period } }),
  subscriptionsHealth: (period: string) =>
    analyticsRequest('/analytics/subscriptions/health', { params: { period } }),
  customers: (period: string) =>
    analyticsRequest('/analytics/customers', { params: { period } }),
  customerDrilldown: (email: string) =>
    analyticsRequest(`/analytics/customers/${encodeURIComponent(email)}`),
  anomalies: () =>
    analyticsRequest('/analytics/anomalies'),
  freshness: () =>
    analyticsRequest<{ lastSyncedAt: string | null; sourceCount: number }>('/analytics/freshness'),
  tenants: () =>
    analyticsRequest<AnalyticsTenantOption[]>('/analytics/tenants'),
  report: (spec: unknown) =>
    analyticsRequest('/analytics/reports', { method: 'POST', body: spec }),
  aiQuery: (payload: {
    naturalLanguage: string;
    pageContext?: Record<string, unknown>;
    sessionId?: string;
  }) =>
    analyticsRequest<AiQueryResponse>('/analytics/ai/query', {
      method: 'POST',
      body: payload,
    }),
  aiSessions: () =>
    analyticsRequest<{ sessions: AiChatSession[] }>('/analytics/ai/sessions'),
  metricsValidate: (sqlTemplate: string) =>
    analyticsRequest<{ valid: boolean; error?: string }>('/analytics/metrics/validate', {
      method: 'POST',
      body: { sqlTemplate },
    }),
};
